import React, { useState } from 'react';
import { Shirt, Gem, Watch, Coffee, Trophy, Circle, Check } from 'lucide-react';
import { UserProfile } from '../../types';

interface StyleOnboardingProps {
  profile: UserProfile;
  onComplete: () => void;
}

export const StyleOnboarding: React.FC<StyleOnboardingProps> = ({ profile, onComplete }) => {
  const [selectedStyles, setSelectedStyles] = useState<string[]>(profile.stylePreferences || []);
  const [loading, setLoading] = useState(false);
  
  const styles = [
    { id: 'streetwear', label: 'Streetwear', icon: Shirt },
    { id: 'high_fashion', label: 'High Fashion', icon: Gem },
    { id: 'vintage', label: 'Vintage', icon: Watch },
    { id: 'minimalist', label: 'Minimalist', icon: Circle },
    { id: 'casual', label: 'Casual', icon: Coffee },
    { id: 'sportswear', label: 'Sportswear', icon: Trophy },
  ];

  const toggleStyle = (id: string) => {
    if (selectedStyles.includes(id)) {
      setSelectedStyles(selectedStyles.filter(s => s !== id));
    } else {
      if (selectedStyles.length < 5) {
        setSelectedStyles([...selectedStyles, id]);
      }
    }
  };

  const handleSave = async () => {
    setLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      onComplete();
    } catch (error) { 
      console.warn("Styles saved locally"); 
    } finally { 
      setLoading(false); 
    }
  };

  return (
    // 🟢 THE FIX: 'fixed inset-0' breaks it out of any constrained parent containers
    <div className="fixed inset-0 z-[200] bg-black text-white flex flex-col justify-start lg:justify-center overflow-y-auto p-6 lg:p-16 animate-in fade-in slide-in-from-right duration-500">
      
      {/* 🟢 EXPANDED MASTER WIDTH: 'max-w-6xl' allows it to stretch massive on desktop */}
      <div className="max-w-6xl mx-auto w-full flex flex-col min-h-full lg:min-h-0">
        
        {/* EDITORIAL HEADER */}
        <div className="mt-8 lg:mt-0 mb-10 lg:mb-16 border-b border-zinc-800 pb-8 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
              <h2 className="text-4xl lg:text-7xl font-black text-white uppercase italic tracking-tighter leading-none mb-3">
                Define<br/>Vibe
              </h2>
              <p className="text-[10px] lg:text-xs font-mono uppercase tracking-[0.3em] text-zinc-500">
                Select up to 5 aesthetics.
              </p>
          </div>
          {/* Hybrid Corner Counter Pill */}
          <div className="text-[10px] lg:text-xs font-mono font-black uppercase tracking-[0.2em] text-zinc-600 bg-zinc-950 border border-zinc-800 px-5 py-3 rounded-full lg:rounded-none w-fit">
              [ {selectedStyles.length} / 5 SELECTED ]
          </div>
        </div>
        
        {/* HYBRID BRUTALIST GRID */}
        {/* 🟢 Massive Desktop Spacing: 'lg:gap-10' spreads the tiles wide */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-10 mb-12">
          {styles.map(style => { 
            const Icon = style.icon; 
            const isSelected = selectedStyles.includes(style.id);
            const isDisabled = !isSelected && selectedStyles.length >= 5;
            
            return (
              <button 
                key={style.id} 
                onClick={() => toggleStyle(style.id)}
                disabled={isDisabled}
                className={`relative p-8 lg:p-16 rounded-2xl lg:rounded-none border transition-all duration-300 flex flex-col items-center justify-center gap-4 group ${
                  isSelected 
                    ? 'bg-white border-white text-black shadow-xl scale-[1.02] z-10' 
                    : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-zinc-500 hover:text-white'
                } ${isDisabled ? 'opacity-30 cursor-not-allowed hover:border-zinc-800 hover:text-zinc-500' : ''}`}
              >
                {/* Icons scale up massive on desktop */}
                <Icon size={36} strokeWidth={isSelected ? 2.5 : 1.5} className="mb-2 lg:mb-6 transition-transform group-hover:scale-110 lg:w-16 lg:h-16" />
                <span className="font-black text-[10px] lg:text-lg uppercase tracking-widest text-center w-full truncate">
                  {style.label}
                </span>
                
                {/* Sharp Selection Indicator */}
                {isSelected && (
                  <div className="absolute top-4 right-4 lg:top-6 lg:right-6 text-black">
                    <Check size={20} strokeWidth={3} className="lg:w-8 lg:h-8" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
        
        {/* ACTION FOOTER */}
        <div className="mt-auto lg:mt-8 pt-8 border-t border-zinc-800 pb-safe">
          <button 
            onClick={handleSave} 
            disabled={selectedStyles.length === 0 || loading}
            className={`w-full py-5 lg:py-8 font-black uppercase tracking-[0.2em] text-xs lg:text-base transition-all flex items-center justify-center rounded-full lg:rounded-none ${
              selectedStyles.length === 0 || loading
              ? 'bg-zinc-950 text-zinc-600 cursor-not-allowed border border-zinc-800' 
              : 'bg-white text-black hover:bg-zinc-200 shadow-xl'
            }`}
          >
            {loading ? 'Transmitting...' : 'Confirm Selection'}
          </button>
          <p className="text-center text-zinc-600 font-mono text-[10px] lg:text-xs uppercase tracking-widest mt-6">
            Preferences can be altered in configuration.
          </p>
        </div>

      </div>
    </div>
  );
};